import type { ReactNode } from 'react';
import { Menu, MenuItem, MenuSeparator } from '@strivelabs/ui-core/v2';
import { cn } from '@strivelabs/ui-core/lib/utils';

/** One choice a property row offers. */
export interface PickerOption {
  value: string;
  label: string;
  node: ReactNode;
}

/**
 * The resting shape of an editable value.
 *
 * Nothing about it says "field" until the pointer is over it, which is what
 * lets seven properties sit in a column without reading as a form.
 */
export const INLINE_FIELD = 'inline-flex max-w-full items-center gap-[7px] whitespace-nowrap rounded-md px-[9px] py-1 -mx-[9px] hover:bg-[#F6F5F1]';

interface TaskInlinePickerProps {
  label: string;
  /** What the value reads as when the picker is closed. */
  children: ReactNode;
  options: PickerOption[];
  selected: string | null;
  /** Offered only where the property may hold nobody, which the assignee may not. */
  clearLabel?: string;
  disabled?: boolean;
  onSelect: (value: string | null) => void;
}

/**
 * A property whose value is chosen from a list.
 *
 * Status, assignee and reviewer are the same interaction wearing different
 * values, so they are the same component: one dropdown that opens over the
 * value it is changing, rather than three that each drift.
 */
export const TaskInlinePicker = ({
  label, children, options, selected, clearLabel, disabled = false, onSelect,
}: TaskInlinePickerProps) => (
  <Menu
    aria-label={label}
    disabled={disabled}
    contentClassName="min-w-[210px] max-h-[300px]"
    trigger={
      <button
        type="button"
        aria-label={label}
        disabled={disabled}
        className={cn(INLINE_FIELD, disabled && 'pointer-events-none')}
      >
        {children}
      </button>
    }
  >
    {options.map((option) => (
      <MenuItem
        key={option.value}
        label={option.label}
        className={cn(
          'gap-2.5 px-[9px] py-[7px] text-[13.5px]',
          option.value === selected && 'bg-[#FBFAF7]',
        )}
        onClick={() => onSelect(option.value)}
      >
        {option.node}
      </MenuItem>
    ))}

    {clearLabel && selected ? (
      <>
        <MenuSeparator />
        <MenuItem
          className="gap-2.5 px-[9px] py-[7px] text-[13.5px] text-[#9A9A9A]"
          onClick={() => onSelect(null)}
        >
          {clearLabel}
        </MenuItem>
      </>
    ) : null}
  </Menu>
);
